/**
 * Address format configuration for internationalization support
 */
export type AddressFormat = {
  display: AddressDisplayFormat;
  form: AddressFormConfig;
};

/**
 * Display templates using {{placeholder}} syntax to format addresses
 *
 */
type AddressDisplayFormat = {
  single: string;
  multi: string[];
};

/**
 * Form config for address input
 */
type AddressFormConfig = {
  /**
   * Form layout grid structure
   * @description Defines the visual arrangement of form fields in rows and columns
   * @example
   * // Single column layout for all fields:
   * [["address1"], ["address2"], ["city"], ["province"], ["postalCode"]]
   *
   * // Two-column layout for province and postalCode:
   * [["address1"], ["address2"], ["city"], ["province","postalCode"]]
   */
  layout: string[][];

  /** Optional validation rules per field */
  validation?: Record<string, AddressValidation>;

  /** Province/state options for select dropdown if applicable */
  provinces?: string[];
};

export type AddressValidation = {
  required: boolean;
  pattern?: string;
};

export type AddressFormats = Record<string, AddressFormat>;

export const ADDRESS_FORMATS: AddressFormats = {
  AF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AX: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  DZ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AD: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AO: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  AI: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  AG: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  AR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  AM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AW: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  AC: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AU: {
    display: {
      single: '{address1},{ address2,} {city} {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {province} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
      provinces: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
    },
  },
  AT: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AZ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BS: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  BH: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BD: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BB: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BZ: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  BJ: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  BM: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BT: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BO: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  BA: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BW: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BV: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BR: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {province}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  IO: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  VG: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BN: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BG: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BF: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  BI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  KH: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  CA: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}, {province} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
      validation: {
        address1: { required: true },
        address2: { required: false },
        city: { required: true },
        province: { required: true },
        postalCode: {
          required: true,
          pattern: '^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$',
        },
      },
      provinces: [
        'AB',
        'BC',
        'MB',
        'NB',
        'NL',
        'NS',
        'NT',
        'NU',
        'ON',
        'PE',
        'QC',
        'SK',
        'YT',
      ],
    },
  },
  CV: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BQ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  KY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  TD: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  CL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  CN: {
    display: {
      single: '{address1}{address2}{city}{postalCode}{province}',
      multi: [
        '{name}',
        '{company}',
        '{address1} {address2} {city}',
        '{postalCode} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  CX: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CC: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CO: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  KM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  CG: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  CD: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  CK: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  CR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  HR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CW: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  CY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CZ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  DK: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  DJ: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  DM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  DO: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  EC: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  EG: {
    display: {
      single: '{address1},{ address2,} {province} {city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{province}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  SV: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GQ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  ER: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  EE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SZ: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  ET: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  FK: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  FO: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  FJ: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  FI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  FR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GA: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  GM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  GE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  DE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GH: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  GI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GD: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  GP: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GT: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {province}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  GG: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GN: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  GW: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  GY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  HT: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  HM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  HN: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  HK: {
    display: {
      single: '{address1}{, address2}{, city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{province} {country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['province']],
    },
  },
  HU: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  IS: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  IN: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  ID: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{province} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  IQ: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  IE: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{province}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  IM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  IL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  IT: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  JM: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  JP: {
    display: {
      single: '〒{postalCode} {province}{city}{address1} {address2}',
      multi: [
        '{country} 〒{postalCode}',
        '{province} {city}',
        '{address1}',
        '{address2}',
        '{company}',
        '{name}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  JE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  JO: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  KZ: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  KE: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  KI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  XK: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  KW: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  KG: {
    display: {
      single: '{postalCode} {city}, {address2,} {address1}, {company}',
      multi: [
        '{postalCode} {city}',
        '{address2}',
        '{address1}',
        '{company}',
        '{name}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LA: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LV: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LB: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LS: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  LI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LT: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  LU: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MO: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  MG: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MW: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  MY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  MV: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  ML: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  MT: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MQ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MU: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  YT: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MX: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  MD: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MC: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MN: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  ME: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MS: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  MA: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MZ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MM: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  NA: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  NR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  NP: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  NL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  NC: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  NZ: {
    display: {
      single: '{address1},{ address2,} {province} {city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{province}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  NI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  NE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  NG: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  NU: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  NF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MK: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  NO: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  OM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PK: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PS: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PA: {
    display: {
      single: '{address1}{ address2}{, city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{province}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['province']],
    },
  },
  PG: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PE: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode} {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  PH: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  PN: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PT: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode} {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  QA: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  RE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  RO: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  RU: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{province}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  RW: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  WS: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  SM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  ST: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  SA: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SN: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  RS: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SC: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  SL: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  SG: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SX: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  SK: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SI: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SB: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  SO: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  ZA: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{province}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  GS: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  KR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  SS: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  ES: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{province}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  LK: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  BL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SH: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  KN: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  LC: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  MF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  PM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  VC: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SD: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  SJ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  SE: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  CH: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TW: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TJ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TZ: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TH: {
    display: {
      single: '{address1}{ address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{province} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  TL: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  TG: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  TK: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  TO: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  TT: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  TA: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TN: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TR: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TM: {
    display: {
      single: '{postalCode}{city}, {country} {address1},{ address2,}',
      multi: [
        '{postalCode}{city}',
        '{country}',
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TC: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  TV: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  UM: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
    },
  },
  UG: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  UA: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  AE: {
    display: {
      single: '{address1}{ address2}{, city}, {province}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {province}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['province']],
    },
  },
  GB: {
    display: {
      single: '{address1},{ address2,} {city} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  US: {
    display: {
      single: '{address1}{, address2}{, city}, {province} {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}, {province} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [
        ['address1'],
        ['address2'],
        ['city'],
        ['province', 'postalCode'],
      ],
      validation: {
        address1: { required: true },
        address2: { required: false },
        city: { required: true },
        province: { required: true },
        postalCode: { required: true, pattern: '^\\d{5}(-\\d{4})?$' },
      },
      provinces: [
        'AA',
        'AE',
        'AL',
        'AK',
        'AP',
        'AS',
        'AZ',
        'AR',
        'CA',
        'CO',
        'CT',
        'DE',
        'DC',
        'FM',
        'FL',
        'GA',
        'GU',
        'HI',
        'ID',
        'IL',
        'IN',
        'IA',
        'KS',
        'KY',
        'LA',
        'ME',
        'MH',
        'MD',
        'MA',
        'MI',
        'MN',
        'MP',
        'MS',
        'MO',
        'MT',
        'NE',
        'NV',
        'NH',
        'NJ',
        'NM',
        'NY',
        'NC',
        'ND',
        'OH',
        'OK',
        'OR',
        'PW',
        'PA',
        'PR',
        'RI',
        'SC',
        'SD',
        'TN',
        'TX',
        'UT',
        'VT',
        'VI',
        'VA',
        'WA',
        'WV',
        'WI',
        'WY',
      ],
    },
  },
  UY: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  UZ: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  VU: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  VA: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  VE: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  VN: {
    display: {
      single: '{address1}{ address2}{, city}, {postalCode}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city} {postalCode}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  WF: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  EH: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  YE: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
  ZM: {
    display: {
      single: '{address1},{ address2,} {postalCode} {city}',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{postalCode} {city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city'], ['postalCode']],
    },
  },
  ZW: {
    display: {
      single: '{address1}{ address2}{, city},',
      multi: [
        '{name}',
        '{company}',
        '{address1}',
        '{address2}',
        '{city}',
        '{country}',
      ],
    },
    form: {
      layout: [['address1'], ['address2'], ['city']],
    },
  },
};
